// App-session heartbeat: the class-2 diagnostic.
//
// "App session" = the life of one running app instance (page load -> renderer
// death/reload), NOT the shopper `sessionId` (which resets per shopper, on
// idle). One app session spans many shopper sessions.
//
// The renderer-death/hang white screens kill the page's JS, so the inline
// watchdog (which is boot-time only) never sees them. This heartbeat captures
// the RUN-UP instead: every ~15s while the app is live it writes a compact
// record to localStorage - how long this app session has been alive, the
// JS-heap trajectory, and video decode-quality counters. It is NOT logged live
// (the crash would kill that); instead the NEXT boot drains it via
// `logAppStart`, so every recovery boot reports how its predecessor died.
//
// This is what tells us whether the crashes are a SLOW leak (long `aliveMs`,
// climbing `peakHeapMb`, rising dropped/corrupted frames -> a video/JS leak we
// can recycle away) or a FAST crash (tiny `aliveMs` -> GPU/decode or OS-level,
// not preventable in-page). Caveat: `performance.memory` measures the JS heap
// only, not GPU/decoder memory - so a flat heap next to a short `aliveMs`
// itself points at the video/GPU path.
import { LocalStorageKeys } from "@is/constants";
import { LocalStorageService } from "@is/services";

type HeapReading = { heapMb: number; heapLimitMb: number };
type VideoReading = { total: number; dropped: number; corrupted: number };

export type HeartbeatRecord = {
  startedAt: number;
  lastAt: number;
  ticks: number;
  startHeapMb?: number;
  lastHeapMb?: number;
  peakHeapMb?: number;
  heapLimitMb?: number;
  video?: VideoReading;
};

export type HeartbeatSummary = {
  aliveMs: number;
  ticks: number;
  startHeapMb?: number;
  lastHeapMb?: number;
  peakHeapMb?: number;
  heapLimitMb?: number;
  video?: VideoReading;
};

const DEFAULT_INTERVAL_MS = 15000;

// Fold a fresh reading into the running record. Pure - keeps `startedAt` and
// `startHeapMb` from the first tick, advances `lastAt`, and tracks the peak
// heap so a mid-session dip can't hide the high-water mark.
export function updateHeartbeat(
  prev: HeartbeatRecord | null,
  now: number,
  heap?: HeapReading,
  video?: VideoReading
): HeartbeatRecord {
  const heapMb = heap?.heapMb;

  if (!prev) {
    return {
      startedAt: now,
      lastAt: now,
      ticks: 1,
      startHeapMb: heapMb,
      lastHeapMb: heapMb,
      peakHeapMb: heapMb,
      heapLimitMb: heap?.heapLimitMb,
      video,
    };
  }

  return {
    startedAt: prev.startedAt,
    lastAt: now,
    ticks: prev.ticks + 1,
    startHeapMb: prev.startHeapMb,
    lastHeapMb: heapMb ?? prev.lastHeapMb,
    peakHeapMb:
      heapMb != null
        ? Math.max(prev.peakHeapMb ?? heapMb, heapMb)
        : prev.peakHeapMb,
    heapLimitMb: heap?.heapLimitMb ?? prev.heapLimitMb,
    video: video ?? prev.video,
  };
}

function safeCall<T>(fn: () => T | undefined): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function readRecord(): HeartbeatRecord | null {
  try {
    const raw = LocalStorageService.getItem(
      LocalStorageKeys.AppSessionHeartbeat
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as HeartbeatRecord)
      : null;
  } catch {
    return null;
  }
}

// JS-heap size (Chromium/Edge only). Undefined elsewhere (e.g. jsdom).
function readHeap(): HeapReading | undefined {
  try {
    const mem = (
      performance as unknown as {
        memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number };
      }
    ).memory;
    if (!mem) return undefined;
    return {
      heapMb: Math.round(mem.usedJSHeapSize / 1048576),
      heapLimitMb: Math.round(mem.jsHeapSizeLimit / 1048576),
    };
  } catch {
    return undefined;
  }
}

// Decode-quality of the most-played `<video>` on the page (the attract loop).
// Rising dropped/corrupted frames before deaths = GPU/decode distress.
export function readVideoQuality(): VideoReading | undefined {
  try {
    const videos = Array.from(document.querySelectorAll("video"));
    let best: VideoReading | undefined;
    for (const video of videos) {
      const getQuality = (
        video as unknown as {
          getVideoPlaybackQuality?: () => {
            totalVideoFrames?: number;
            droppedVideoFrames?: number;
            corruptedVideoFrames?: number;
          };
        }
      ).getVideoPlaybackQuality;
      if (typeof getQuality !== "function") continue;
      const quality = getQuality.call(video);
      const total = quality?.totalVideoFrames ?? 0;
      if (!best || total > best.total) {
        best = {
          total,
          dropped: quality?.droppedVideoFrames ?? 0,
          corrupted: quality?.corruptedVideoFrames ?? 0,
        };
      }
    }
    return best;
  } catch {
    return undefined;
  }
}

// One heartbeat tick: fold current readings into the stored record. Readers are
// injectable for testing; each is individually guarded so a failing reader can
// never break the tick (telemetry must never break the app).
export function writeAppSessionHeartbeat(
  now: () => number = Date.now,
  heap: () => HeapReading | undefined = readHeap,
  video: () => VideoReading | undefined = readVideoQuality
): void {
  try {
    const next = updateHeartbeat(
      readRecord(),
      now(),
      safeCall(heap),
      safeCall(video)
    );
    LocalStorageService.setItem(
      LocalStorageKeys.AppSessionHeartbeat,
      JSON.stringify(next)
    );
  } catch {
    // best-effort
  }
}

// Read + clear the previous app session's heartbeat, summarised for the App
// start payload. `aliveMs` = how long the (rendered) app lived before it died.
export function drainAppSessionHeartbeat(): HeartbeatSummary | undefined {
  const record = readRecord();
  try {
    LocalStorageService.removeItem(LocalStorageKeys.AppSessionHeartbeat);
  } catch {
    // best-effort
  }
  if (
    !record ||
    typeof record.startedAt !== "number" ||
    typeof record.lastAt !== "number"
  ) {
    return undefined;
  }
  return {
    aliveMs: record.lastAt - record.startedAt,
    ticks: record.ticks,
    startHeapMb: record.startHeapMb,
    lastHeapMb: record.lastHeapMb,
    peakHeapMb: record.peakHeapMb,
    heapLimitMb: record.heapLimitMb,
    video: record.video,
  };
}

let heartbeatTimer: ReturnType<typeof setInterval> | undefined;

// Begin ticking for the current app session. Call once the app has rendered
// (markAppStartCompleted) so the record represents "time since a healthy
// render". Idempotent; the immediate first tick stamps the render moment.
export function startAppSessionHeartbeat(
  intervalMs: number = DEFAULT_INTERVAL_MS
): void {
  try {
    if (heartbeatTimer) return;
    writeAppSessionHeartbeat();
    heartbeatTimer = setInterval(() => writeAppSessionHeartbeat(), intervalMs);
    // Don't let the interval keep a Node process (test runner) alive; no-op in
    // the browser where the timer id is a number.
    (heartbeatTimer as unknown as { unref?: () => void }).unref?.();
  } catch {
    // best-effort
  }
}
